// Helpers used in the employer calendar workflow, mostly by CalendarBase and AppointmentSlots

import _ from "lodash";
import moment from "moment-timezone";

import {
  INTERVIEW_REQUEST_OUTSTANDING_STATES,
  CANDIDATE_REQUESTS_MORE_AVAILABILITY,
  CANDIDATE_REQUESTS_RESCHEDULE,
} from "common/helpers/interviewRequest";

export const APPOINTMENT_SLOT_INTERVAL_IN_MINUTES = 30;
export const CREATE_SLOT = Symbol("create");
export const REMOVE_SLOT = Symbol("remove");

export const TIMEZONES = {
  "America/New_York": "Eastern",
  "America/Chicago": "Central",
  "America/Denver": "Mountain",
  "America/Phoenix": "Mountain AZ",
  "America/Los_Angeles": "Pacific",
  "America/Juneau": "Alaska",
  "Pacific/Honolulu": "Hawaii",
  "Pacific/Guam": "Guam",
};

export const AVAILABILITY_BUFFER_TYPES = [
  {
    value: "next_workday",
    title: "Next business day (default)",
    bookable: "Next business day",
    info: "Candidates can schedule interviews starting from the next business day onward",
  },
  {
    value: "6_hours",
    title: "6 hour buffer",
    bookable: "6 hr in advance",
    info: "Candidates can schedule interviews with you 6 hours in advance",
  },
  {
    value: "4_hours",
    title: "4 hour buffer",
    bookable: "4 hr in advance",
    info: "Candidates can schedule interviews with you 4 hours in advance",
  },
  {
    value: "2_hours",
    title: "2 hour buffer",
    bookable: "2 hr in advance",
    info: "Candidates can schedule interviews with you 2 hours in advance",
  },
];

export const getAvailabilityType = (type) =>
  AVAILABILITY_BUFFER_TYPES.find((buffer) => buffer.value === type) ||
  AVAILABILITY_BUFFER_TYPES[0];

export const getBookableText = (type) => getAvailabilityType(type).bookable;

// create a map of appointment slots keyed by start_time
export const appSlotStartMap = (appSlots) =>
  new Map(appSlots.map((s) => [s.start_time, s]));

// create a fresh appointment slot instance for a create/delete
export const appSlot = (start_time) => {
  const end_time = moment
    .unix(start_time)
    .add(APPOINTMENT_SLOT_INTERVAL_IN_MINUTES, "minutes")
    .unix();

  return {
    taken: false,
    start_time,
    end_time,
  };
};

// link app slot updates to extant slots for graphql
export const appSlotUpdate = (start_time, type, existingAppSlots) => {
  const update = {
    ...appSlot(start_time),
    _destroy: type === REMOVE_SLOT,
  };

  // not needed for graphql
  delete update.taken;

  if (existingAppSlots.has(start_time)) {
    update.id = existingAppSlots.get(start_time).id;
  }

  return update;
};

// check whether an interview is outstanding, always false for request being rescheduled
export const requestIsOutstanding = (ir, toIgnore) => {
  if (
    ir.step !== "outstanding" &&
    !_.includes(INTERVIEW_REQUEST_OUTSTANDING_STATES, ir.step)
  ) {
    return false;
  }
  if (ir.id === undefined || toIgnore === undefined) {
    return true;
  }

  return ir.id !== toIgnore.id;
};

export const requestNeedsMoreAvailability = (interviewRequest) => {
  return interviewRequest.step === CANDIDATE_REQUESTS_MORE_AVAILABILITY;
};

export const requestNeedsReschedule = (interviewRequest) => {
  return interviewRequest.step === CANDIDATE_REQUESTS_RESCHEDULE;
};

// sorts by 'start' or 'start_time' properties (must all be one or the other, not a mix), ascending
const sortByStart = (items) => {
  if (items.length <= 1) {
    return [...items];
  }

  const startKey = items[0].start_time !== undefined ? "start_time" : "start";

  return [...items].sort((a, b) => {
    if (a[startKey] >= b[startKey]) {
      return -1;
    }
    if (a[startKey] < b[startKey]) {
      return 1;
    }

    return 0;
  });
};

export const isMidnight = (date) =>
  (date.getHours() === 0 || date.getHours === 24) &&
  date.getMinutes() === 0 &&
  date.getSeconds() === 0;

// Due to Date always evaluating in the browser timezone, the following 2 methods will return Dates
// shifted forwards/backwards in actual time to force the appropriate change

// Takes a time in the browsers timezone and returns a Date shifted to the desired timezone
export const mapTimeToTimeZone = (localTime, tz) => {
  const adjustedTime = moment(localTime)
    .tz(tz)
    .format("YYYY/MM/DD HH:mm:ss ZZ");

  // Drop the timezone, leaving only adjusted YYYY/MM/DD HH:mm:ss
  return new Date(adjustedTime.slice(0, -6));
};

// Takes a time tied to the specified timezone and shifts it to the browsers timezone
export const mapTimeFromTimeZone = (adjustedTime, tz, offset = 0) => {
  const TZ = moment().tz(tz).format("ZZ");
  const noTimeZone = moment(adjustedTime).add(offset, "minutes");
  return moment(
    `${noTimeZone.format("YYYY/MM/DD HH:mm:ss")} ${TZ}`,
    "YYYY/MM/DD HH:mm:ss ZZ"
  );
};

export const getTimezoneForCalendar = (calendar) => {
  let timeZoneName = (calendar && calendar.time_zone_name) || moment.tz.guess();

  if (TIMEZONES[timeZoneName] === undefined) {
    timeZoneName = "America/New_York";
  }

  return timeZoneName;
};

// Takes an array of calendar items with a start and end time, (either appointment slots or rbc events),
// and merges continuous runs of availability/an interview into a single item. start/end times can be Dates, moments,
// or even just a timestamp, but they must be consistent across the input.
const mergeContinuousRuns = (items, tz) => {
  if (items.length <= 1) {
    return [...items];
  }

  const merged = [];
  const sortedItems = sortByStart(items);

  merged.push(sortedItems.pop());

  const startKey =
    sortedItems[0].start_time !== undefined ? "start_time" : "start";
  const endKey = sortedItems[0].end_time !== undefined ? "end_time" : "end";

  while (sortedItems.length) {
    const prev = merged[merged.length - 1];
    const next = sortedItems.pop();

    const bothOpen = !prev.taken && !next.taken;

    // we split rbc events at current time for styling
    const prevContainsNow =
      +mapTimeFromTimeZone(prev.start, tz) < Date.now() &&
      +mapTimeFromTimeZone(prev.end, tz) >= Date.now();

    let prevEndAsDate = prev[endKey];
    let nextStartAsDate = next[startKey];

    if (typeof prev[endKey] === "number") {
      prevEndAsDate = new Date(prev[endKey] * 1000);
      nextStartAsDate = new Date(next[startKey] * 1000);
    } else if (!(prev[endKey] instanceof Date)) {
      prevEndAsDate = prev[endKey].toDate();
      nextStartAsDate = next[startKey].toDate();
    }

    // split rbc events on midnight so they get sent to the proper day columns
    const straddlesMidnight =
      isMidnight(prevEndAsDate) || isMidnight(nextStartAsDate);

    if (
      bothOpen &&
      !prevContainsNow &&
      !straddlesMidnight &&
      next[startKey] <= prev[endKey]
    ) {
      prev[endKey] = next[endKey] >= prev[endKey] ? next[endKey] : prev[endKey];
    } else if (
      !straddlesMidnight &&
      prev.taken &&
      next.taken &&
      prev.interview_id === next.interview_id
    ) {
      prev[endKey] = next[endKey];
    } else {
      merged.push(next);
    }
  }

  return merged;
};

// Takes an array of appointment slots (linked to interviews), joins continuous runs of availability/interview,
// and maps those runs to rbc events for the specified timezone
export const mapAppSlotsToRbcEvents = (appSlots, tz) => {
  const rbcEvents = appSlots.map(
    ({ start_time, end_time, interview_id, taken, title }) => ({
      start: mapTimeToTimeZone(new Date(start_time * 1000), tz),
      end: mapTimeToTimeZone(new Date(end_time * 1000), tz),
      title: taken ? title : "Available",
      interview_id,
      taken,
    })
  );

  const mergedRbcEvents = mergeContinuousRuns(rbcEvents, tz);

  mergedRbcEvents.forEach((event, i, arr) => {
    if (i === 0) {
      return;
    }

    // needed to determine whether event may need a css border-top
    event.adjacentEventAbove =
      arr[i - 1].end.getTime() === event.start.getTime();
  });

  return mergedRbcEvents;
};

// Converts a variable length rbc event into an array of fixed length appointment slots
export const mapRbcEventToAppSlots = (rbcEvent, tz, offset = 0) => {
  const rbcEventEnd = mapTimeFromTimeZone(rbcEvent.end, tz, offset).unix();

  const appSlots = [];
  const timestep = moment
    .duration(APPOINTMENT_SLOT_INTERVAL_IN_MINUTES, "minutes")
    .asSeconds();

  let currStart = mapTimeFromTimeZone(rbcEvent.start, tz, offset).unix();

  for (; currStart < rbcEventEnd; currStart += timestep) {
    appSlots.push({
      start_time: currStart,
      end_time: currStart + timestep,
      taken: rbcEvent.taken,
    });
  }

  return appSlots;
};

// Finds the length of each continuous availability block in supplied appSlots, returns a set of those lengths
export const findAvailabilityBlockLengthsHours = (appSlots, tz) => {
  const blockLengths = new Set();

  mergeContinuousRuns(appSlots, tz).forEach((block) => {
    const lengthHours = (block.end_time - block.start_time) / 60 / 60;
    blockLengths.add(lengthHours);
  });

  return [...blockLengths];
};
